/**
 * <mango-job-detail> — the work-order offer / detail screen.
 * =====================================================================
 * Retrieves one work order by short_name (set via the `data-short-name`
 * attribute the router supplies), binds its lifecycle / weights / sow_rate /
 * deliverables, and exposes the offered-stage transitions accept / decline.
 *
 * ROLE-GATING IS COSMETIC (server enforces): when the serialized work order omits
 * `sow_rate` (a Provider-Staff response), the financial card is removed and the
 * accept/decline panel is replaced by the Admin-action staff gate — gating on
 * field presence, never a client-side role flag.
 *
 * accept is fired only after the provider types a signature name (the prototype's
 * sign-to-accept gate) — purely a UX guard; the server is the authority.
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field; no bind references it.
 */
import { getWorkOrder, type WorkOrder } from "../api/client";
import { applyBindings, wireActions } from "../bind";
import { loadView, hasFinancials, money, date, setText, toggle } from "./shared";

const VIEW_URL = new URL("../../views/job-detail.html", import.meta.url).href;

export class MangoJobDetail extends HTMLElement {
  static observedAttributes = ["data-short-name"];
  private disposeActions: (() => void) | null = null;
  private job: WorkOrder | null = null;

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    this.disposeActions = wireActions(this, {
      // accept requires a typed signature, sent as the persisted sign-off; decline
      // fires immediately with no body.
      getBody: (ev) => {
        if (ev.action === "accept") {
          const name = this.querySelector<HTMLInputElement>("[data-sign-name]")?.value.trim() ?? "";
          if (!name) {
            this.setStatus("Type your full name to sign before accepting.");
            return false;
          }
          return { signoff_name: name };
        }
        return undefined; // decline takes no body
      },
      onSuccess: (ev) => {
        // accept → go to the field brief; decline → back to the list.
        if (ev.action === "decline") window.location.hash = "#/jobs";
        else window.location.hash = `#/brief/${encodeURIComponent(ev.id)}`;
      },
      onError: (ev, error) => {
        console.error(`[mango-job-detail] ${ev.action} failed for ${ev.id}`, error);
        this.setStatus(`Could not ${ev.action} this work order. Please retry.`);
      },
    });

    this.wireSignature();
    await this.load();
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) void this.load();
  }

  private get shortName(): string {
    return this.getAttribute("data-short-name") ?? "";
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  /** Enable Accept only once a signature name is typed (UX guard). */
  private wireSignature(): void {
    const input = this.querySelector<HTMLInputElement>("[data-sign-name]");
    const preview = this.querySelector<HTMLElement>("[data-sign-preview]");
    const btn = this.querySelector<HTMLButtonElement>("[data-accept-btn]");
    input?.addEventListener("input", () => {
      const v = input.value.trim();
      if (preview) preview.textContent = v;
      if (btn) btn.disabled = v.length === 0;
    });
  }

  private async load(): Promise<void> {
    const id = this.shortName;
    if (!id) {
      this.setStatus("No work order specified.");
      return;
    }
    this.setStatus("Loading work order…");
    try {
      this.job = await getWorkOrder(id);
    } catch (error) {
      console.error("[mango-job-detail] failed to load", error);
      this.setStatus("Could not load this work order.");
      return;
    }
    this.render();
  }

  private render(): void {
    const job = this.job;
    if (!job) return;

    applyBindings(this, { job });
    this.formatBoundValues(job);

    const isOffered = job.provider_status === "offered";

    // Two modes: offer-review (offered) vs read-only summary (everything else).
    toggle(this, "[data-offered-view]", isOffered);
    toggle(this, "[data-progress-view]", !isOffered);
    setText(this, "[data-detail-title]", isOffered ? "Work order offer" : "Work order");

    // Offered view — financials + accept/sign are Admin-only (sow_rate presence).
    const showFinancials = hasFinancials(job) && job.sow_rate != null;
    toggle(this, "[data-financials]", showFinancials);
    const canAct = isOffered && hasFinancials(job);
    toggle(this, "[data-offer-actions]", canAct);
    // Staff sees the gate on an offered WO they cannot act on (financials absent).
    toggle(this, "[data-staff-gate]", isOffered && !hasFinancials(job));

    // Approximate equipment scope — client-booking estimates; show only when present.
    const hasEstimate = job.estimated_device_count != null || job.estimated_ewaste_lbs != null;
    toggle(this, "[data-equipment-scope]", isOffered && hasEstimate);
    setText(this, '[data-bind="job.estimated_device_count"]',
      job.estimated_device_count == null ? "—" : `~${job.estimated_device_count}`);
    setText(this, '[data-bind="job.estimated_ewaste_lbs"]',
      job.estimated_ewaste_lbs == null ? "—" : `~${job.estimated_ewaste_lbs} lbs`);

    // Progress view — weight rows hide when null; render the deliverable ledger.
    toggle(this, "[data-weight-total-row]", job.weight_total != null);
    toggle(this, "[data-weight-ewaste-row]", job.weight_ewaste != null);
    this.renderDeliverables(job);
    this.setStatus("");
  }

  /** Re-format the money/date bindings (applyBindings wrote raw values). */
  private formatBoundValues(job: WorkOrder): void {
    const fmt = (sel: string, text: string): void => {
      this.querySelectorAll<HTMLElement>(sel).forEach((el) => (el.textContent = text));
    };
    fmt('[data-bind="job.sow_rate"]', money(job.sow_rate));
    fmt('[data-bind="job.weight_total"]', job.weight_total == null ? "—" : job.weight_total);
    fmt('[data-bind="job.weight_ewaste"]', job.weight_ewaste == null ? "—" : job.weight_ewaste);
    const dates: Array<[string, string | null]> = [
      ["offered_at", job.offered_at],
      ["offer_expires_at", job.offer_expires_at],
      ["accepted_at", job.accepted_at],
      ["pickup_scheduled_at", job.pickup_scheduled_at],
      ["onsite_arrived_at", job.onsite_arrived_at],
      ["onsite_completed_at", job.onsite_completed_at],
      ["sow_submitted_at", job.sow_submitted_at],
      ["fmv_submitted_at", job.fmv_submitted_at],
      ["finalized_at", job.finalized_at],
      ["mango_approved_at", job.mango_approved_at],
      ["dropoff_datetime", job.dropoff_datetime],
    ];
    for (const [field, value] of dates) fmt(`[data-bind="job.${field}"]`, date(value));
  }

  private renderDeliverables(job: WorkOrder): void {
    const list = this.querySelector<HTMLElement>("[data-deliverables]");
    const tpl = this.querySelector<HTMLTemplateElement>("template[data-deliverable-row]");
    if (!list || !tpl) return;
    list.replaceChildren();
    if (!job.deliverables.length) {
      list.textContent = "No deliverables defined yet.";
      return;
    }
    for (const d of job.deliverables) {
      const frag = tpl.content.cloneNode(true) as DocumentFragment;
      const item = frag.querySelector<HTMLElement>("[data-deliverable-item]");
      if (item) item.classList.add(d.status === "complete" ? "complete" : "pending");
      setText(frag, "[data-deliverable-name]", d.deliverable_type_display);
      setText(frag, "[data-deliverable-status]", d.status_display);
      list.appendChild(frag);
    }
  }
}

export function registerMangoJobDetail(): void {
  if (!customElements.get("mango-job-detail")) {
    customElements.define("mango-job-detail", MangoJobDetail);
  }
}
